/**
 * Working out what an officer is left with when the counters ran out.
 *
 * When auto-assign can only seat an officer for part of the window they were
 * asked for, the roster offers to re-assign them against the time that was
 * actually coverable. That means subtracting the no-counter windows from the
 * requested one — which is only correct if both are read against the shift's
 * own clock.
 */

import { hmmmToSlot, hmmmToEndSlot } from '@/config/modeConfig';

/** Slot index back to HHMM, on the shift that starts at `startHour`. */
export function slotToHhmm(slot: number, startHour: number): string {
  const totalMins = slot * 15;
  const h = (Math.floor(totalMins / 60) + startHour) % 24;
  const m = totalMins % 60;
  return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
}

/**
 * The timing an officer is left with once the windows that had no counter are
 * taken out of the one they were asked for.
 *
 * Counted in slots from the shift's own start hour, never in clock minutes: on
 * a night shift 0100 comes AFTER 2200, and read as the smaller number
 * "2200-0100" is a window that ends before it begins. That is how a night
 * officer's adjusted window came back empty — and an empty window is dropped
 * from the re-assign, leaving them sat on whatever partial counter they already
 * had instead of the time they were free for.
 *
 * @param timing           the requested window(s), "HHMM-HHMM" comma-separated
 * @param unassignedTimes  the window(s) no counter was free for, same format
 * @param startHour        the shift's start hour (10 morning, 22 night)
 * @returns the remaining window(s) in the same format, or "" if nothing is left
 */
export function computeAssignedTiming(
  timing: string,
  unassignedTimes: string,
  startHour: number,
): string {
  const parseSegs = (value: string): [number, number][] =>
    value.split(',')
      .map(seg => seg.trim())
      .filter(Boolean)
      .map(seg => {
        const [start, end] = seg.split('-');
        return [hmmmToSlot(start, startHour), hmmmToEndSlot(end, startHour)] as [number, number];
      })
      .filter(([start, end]) => end > start);

  let remaining: [number, number][] = parseSegs(timing);
  for (const [gapStart, gapEnd] of parseSegs(unassignedTimes)) {
    remaining = remaining.flatMap(([start, end]): [number, number][] => {
      if (gapEnd <= start || gapStart >= end) return [[start, end]];
      const kept: [number, number][] = [];
      if (start < gapStart) kept.push([start, gapStart]);
      if (gapEnd < end) kept.push([gapEnd, end]);
      return kept;
    });
  }
  // Chronological on the shift's clock, not in the order the ops room happened
  // to type them: "0900-1000,2200-2300" is a night officer's LAST window first.
  return remaining
    .sort((a, b) => a[0] - b[0])
    .map(([start, end]) => `${slotToHhmm(start, startHour)}-${slotToHhmm(end, startHour)}`)
    .join(',');
}

/**
 * The earliest start and latest end across a timing's windows, on the shift's
 * own clock.
 *
 * Not the first and last segment as written: an ops-room line lists the windows
 * in whatever order it likes, and on a night shift "0900-1000,2200-2300" starts
 * at 2200 and ends at 1000 — read positionally it spans 0900 to 2300, which is
 * a range that ends eleven hours before it begins and which the backend rightly
 * refuses.
 *
 * Returns null when there is no usable window at all.
 */
export function timingSpan(
  timing: string,
  startHour: number,
): { start: string; end: string } | null {
  const bounds = timing.split(',')
    .map(seg => seg.trim())
    .filter(Boolean)
    .map(seg => {
      const [start, end] = seg.split('-');
      return [hmmmToSlot(start, startHour), hmmmToEndSlot(end, startHour)] as [number, number];
    })
    .filter(([start, end]) => end > start);
  if (bounds.length === 0) return null;
  return {
    start: slotToHhmm(Math.min(...bounds.map(b => b[0])), startHour),
    end: slotToHhmm(Math.max(...bounds.map(b => b[1])), startHour),
  };
}
